import { Clock, CloudOff, Snowflake } from 'lucide-react'
import type { CappedStateDTO, FreshnessDTO } from '../../../api/portfolio'

// FreshnessChip — spec §5's always-visible freshness chip, beside every
// verdict badge. `model.FreshnessFromState`
// (internal/problems/model/freshness.go) emits exactly FIVE finished EN
// sentences (plan D-7) — this component renders `text` VERBATIM and never
// branches on `state`/`days_since` itself (Global Constraint 2/6: the
// frontend is a pure renderer, never a second source of truth for a
// backend-computed sentence).
//
// The ONE string this component authors is the SIXTH branch — the
// missing-DTO state (freshness absent/null, or an empty/whitespace `text`,
// which is a backend contract violation, not a domain fact). It is
// synthesized from the ABSENCE of a usable `text`, never from inspecting
// `state`/`days_since` values (D-7's honesty rule — `days_since` is `*int`,
// `nil` ≠ `0`; "0" means "today", absent means "we cannot say", and neither
// is ever conflated with "couldn't check").
const LOAD_FAILED_TEXT = "Couldn't check report freshness"

export interface FreshnessChipProps {
  freshness: FreshnessDTO | null | undefined
  size?: 'sm' | 'md'
  testId?: string
}

export function FreshnessChip({
  freshness,
  size = 'md',
  testId,
}: FreshnessChipProps) {
  const text = freshness?.text?.trim()
  const unavailable = !text
  // `text` may be `''` (whitespace-only backend contract violation) —
  // `??` alone wouldn't fall back on that, only on null/undefined.
  const rendered = unavailable ? LOAD_FAILED_TEXT : text

  const textSizeClass = size === 'sm' ? 'text-eg-xs' : 'text-eg-sm'
  const iconSize = size === 'sm' ? 12 : 14
  const Icon = unavailable ? CloudOff : Clock

  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-eg-md border px-2 py-0.5 font-body',
        textSizeClass,
        // Freshness is not a verdict — always the neutral family, never
        // success/danger/amber, regardless of how stale the data is.
        'bg-eg-surface text-eg-text-secondary border-eg-border',
        unavailable ? 'border-dashed' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-label={`Report freshness: ${rendered}`}
      data-testid={testId}
    >
      <Icon size={iconSize} aria-hidden="true" />
      <span>{rendered}</span>
    </span>
  )
}

// CappedStateChip — 86cb6r8gc, spec §3/§6. Same visual chip family as
// FreshnessChip immediately above (same neutral-only classes, same icon
// register, same verbatim-text contract) — deliberately NOT a new
// primitive, just a second data-adapter onto the identical chip grammar,
// per the spec's "reuse FreshnessChip, don't invent a new primitive" rule.
// Renders NOTHING (null) when capped is absent/null — the org isn't
// Free-tier-capped, so there is nothing to show; this is the ONLY branch
// that differs from FreshnessChip (which always renders SOMETHING, even a
// "couldn't check" fallback) because an absent CappedState is a real,
// common, non-error state (every non-Free org, every Free org under cap),
// not a load failure.
//
// variant picks which backend-authored string to render VERBATIM — never
// truncate `text` client-side to fake `terse`:
//   - 'full'  -> capped.text        (hero / domain-detail banner)
//   - 'terse' -> capped.terse_text  (Problems/Forgers/standalone-Forgers/
//                Alerts in-panel chip, Overview/All Domains row)
export interface CappedStateChipProps {
  capped: CappedStateDTO | null | undefined
  variant?: 'full' | 'terse'
  size?: 'sm' | 'md'
  testId?: string
}

export function CappedStateChip({
  capped,
  variant = 'terse',
  size = 'md',
  testId,
}: CappedStateChipProps) {
  if (!capped?.capped) {
    return null
  }
  const rendered = (variant === 'full' ? capped.text : capped.terse_text)?.trim()
  if (!rendered) {
    // Backend contract violation (capped=true with an empty string) — fail
    // silent rather than render a blank chip; the DTO's own `capped`
    // boolean already tells the story elsewhere on the page (e.g.
    // BillingStatusBanner), so this is not an absence-of-evidence gap.
    return null
  }

  const textSizeClass = size === 'sm' ? 'text-eg-xs' : 'text-eg-sm'
  const iconSize = size === 'sm' ? 12 : 14

  return (
    <span
      className={[
        'inline-flex items-center gap-1 rounded-eg-md border px-2 py-0.5 font-body',
        textSizeClass,
        // Never a verdict color (never success/danger/amber) — "we stopped
        // looking" is a different feeling than "you are being attacked
        // right now" (spec §5's domain-detail banner rationale), same
        // neutral-only rule FreshnessChip's own comment states above.
        'bg-eg-surface text-eg-text-secondary border-eg-border border-dashed',
      ].join(' ')}
      aria-label={`Report cap: ${rendered}`}
      data-testid={testId}
    >
      <Snowflake size={iconSize} aria-hidden="true" />
      <span>{rendered}</span>
    </span>
  )
}
